import { Injectable } from '@angular/core';
import { Action, Actions, Selector, State, StateContext, Store } from '@ngxs/store';
import produce from 'immer';
import { Observable } from 'rxjs';
import { BrickSettingsService, DirectoryDetailsBrickSettingRemoteDto } from '../../../../../../../generated/swagger';
import { IBrickSettingStateModel, SpecificBrickSettingsStateBase } from '../specific-brick-settings-base';
import { ChangeShowAvatar, ChangeShowFieldNames, ChangeVisibleFields } from './directory-details-settings.action';

export interface DirectoryDetailsSettingStateModel extends IBrickSettingStateModel<DirectoryDetailsBrickSettingRemoteDto> {}

function getDefaultSpecificBrickSetting(): DirectoryDetailsBrickSettingRemoteDto {
   return {
      commonBrickSettingRemoteDto: null,
      showAvatar: true,
      showFieldNames: false,
      visibleFields: [
         'tbl_org_00_fld_orgnum',
         'tbl_org_00_fld_orgnam',
         'tbl_org_00_fld_email',
         'tbl_org__PRESENCE_STATUS',
         'tbl_org__MOBILE_STATUS',
         'tbl_org__LINE_STATUS',
         'tbl_org__PRESENCE_STATUS_NOTE',
         'tbl_org__PRESENCE_STATUS_FORWARD',
         'tbl_org__APPOINTMENTS',
      ],
   };
}

@Injectable()
@State<DirectoryDetailsSettingStateModel>({
   name: 'directoryDetailsBrickSettings',
   defaults: {
      specificBrickSettings: {
         brickSettings: {},
         brickSettingsEditMode: null,
      },
   },
})
export class DirectoryDetailsSettingState extends SpecificBrickSettingsStateBase<DirectoryDetailsBrickSettingRemoteDto>(getDefaultSpecificBrickSetting) {
   constructor(private brickSettingsService: BrickSettingsService, store: Store, actions$: Actions) {
      super(store);
   }

   @Selector([DirectoryDetailsSettingState, DirectoryDetailsSettingState.getSettingModelFn])
   public static getShowAvatar(state, getSettingModelFn: (state, brickInstanceId) => DirectoryDetailsBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingModelFn(state, brickId).showAvatar;
      };
   }

   @Selector([DirectoryDetailsSettingState, DirectoryDetailsSettingState.getSettingModelFn])
   public static getShowFieldNames(state, getSettingModelFn: (state, brickInstanceId) => DirectoryDetailsBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingModelFn(state, brickId).showFieldNames;
      };
   }

   @Selector([DirectoryDetailsSettingState, DirectoryDetailsSettingState.getSettingModelFn])
   public static getVisibleFields(state, getSettingModelFn: (state, brickInstanceId) => DirectoryDetailsBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingModelFn(state, brickId).visibleFields;
      };
   }

   @Action(ChangeShowAvatar)
   public changeShowAvatar(ctx: StateContext<DirectoryDetailsSettingStateModel>, action: ChangeShowAvatar) {
      const state = produce(ctx.getState(), (draft) => {
         const brickSettingModel = DirectoryDetailsSettingState.getSettingModelOrDefault(draft, action.brickInstanceId);
         brickSettingModel.showAvatar = action.showAvatar;
         draft.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
      });
      ctx.setState(state);
   }

   @Action(ChangeShowFieldNames)
   public changeShowFieldNames(ctx: StateContext<DirectoryDetailsSettingStateModel>, action: ChangeShowFieldNames) {
      const state = produce(ctx.getState(), (draft) => {
         const brickSettingModel = DirectoryDetailsSettingState.getSettingModelOrDefault(draft, action.brickInstanceId);
         brickSettingModel.showFieldNames = action.showFieldNames;
         draft.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
      });
      ctx.setState(state);
   }

   @Action(ChangeVisibleFields)
   public changedVisibleFields(ctx: StateContext<DirectoryDetailsSettingStateModel>, action: ChangeVisibleFields) {
      const state = produce(ctx.getState(), (draft) => {
         const brickSettingModel = DirectoryDetailsSettingState.getSettingModelOrDefault(draft, action.brickInstanceId);
         brickSettingModel.visibleFields = action.visibleFields;
         draft.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
      });
      ctx.setState(state);
   }

   protected doSaveSpecificSettings(brickInstanceId: string, brickSetting: DirectoryDetailsBrickSettingRemoteDto): Observable<any> {
      return this.brickSettingsService.changeDirectoryDetailsBrickSettings({
         brickInstanceId: brickInstanceId,
         settings: brickSetting,
      });
   }
}
